import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './NpcPortrait.css';
import MediaImage from './MediaImage';
import CharacterVitals from './CharacterVitals';

export default function NpcPortrait({ src, label, kind = 'portrait', beingId, loadRelationships }) {
  const [open, setOpen] = useState(false);
  const [relationships, setRelationships] = useState({ loading: false, rows: [], error: false, vitals: null });
  const showRelationships = kind === 'portrait' && Boolean(beingId && loadRelationships);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open || !showRelationships) return undefined;
    let cancelled = false;
    const cached = loadRelationships.peek?.(beingId);
    setRelationships(cached
      ? { loading: false, rows: cached.relationships || [], error: false, vitals: cached.vitals || null }
      : { loading: true, rows: [], error: false, vitals: null });
    Promise.resolve().then(() => loadRelationships(beingId)).then(result => {
      if (!cancelled) setRelationships({ loading: false, rows: result?.relationships || [], error: false, vitals: result?.vitals || null });
    }).catch(() => {
      if (!cancelled && !cached) setRelationships({ loading: false, rows: [], error: true, vitals: null });
    });
    return () => { cancelled = true; };
  }, [open, showRelationships, beingId, loadRelationships]);

  useEffect(() => {
    if (!open) return undefined;
    const overflow = document.body.style.overflow;
    const trigger = triggerRef.current;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'Tab') {
        event.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = overflow;
      trigger?.focus();
    };
  }, [open]);

  return <>
    <button ref={triggerRef} type="button" className="play2-portrait-button"
      aria-label={`Enlarge ${kind} of ${label}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
      <MediaImage src={src} alt={label} />
    </button>
    {open && createPortal(
      <div className="play2-portrait-overlay" onClick={event => { if (event.target === event.currentTarget) setOpen(false); }}>
        <section className={`play2-portrait-modal${showRelationships ? ' play2-portrait-with-relationships' : ''}`} role="dialog" aria-modal="true" aria-label={`${kind === 'portrait' ? 'Portrait' : 'Image'} of ${label}`}>
          <div className="play2-portrait-header">
            <strong>{label}</strong>
            <button ref={closeRef} type="button" className="secondary-button" onClick={() => setOpen(false)}>Close {kind}</button>
          </div>
          <div className="play2-portrait-content">
            <MediaImage src={src} alt={label} size="display" />
            {showRelationships && <section className="play2-portrait-relationships" aria-label="Relationships with you" aria-live="polite">
              {relationships.vitals && <CharacterVitals being={relationships.vitals} />}
              <h4>Relationships with you</h4>
              {relationships.loading ? <p>Loading relationships…</p>
                : relationships.error ? <p>Relationships could not be loaded. Reopen the portrait to try again.</p>
                  : relationships.rows.length === 0 ? <p>No recorded relationships yet.</p>
                    : [['toward_player', 'Their view of you'], ['from_player', 'Your view of them']].map(([direction, heading]) => {
                      const rows = relationships.rows.filter(row => row.direction === direction);
                      return rows.length > 0 && <div key={direction}>
                        <h5>{heading}</h5>
                        <dl>{rows.map(row => <div key={row.relateType}>
                          <dt>{row.relateType}</dt>
                          <dd>{row.score == null || !Number.isFinite(Number(row.score)) ? 'Not scored'
                            : Number(row.score).toLocaleString(undefined, { maximumFractionDigits: 2 })}</dd>
                        </div>)}</dl>
                      </div>;
                    })}
            </section>}
          </div>
        </section>
      </div>, document.body
    )}
  </>;
}
